import { useEffect } from 'react'
import { usePreferences } from '@/shell/stores/preferences'

/**
 * Applies `data-theme` and `data-density` to `<html>` based on the
 * preferences store. When theme is `system`, follows `prefers-color-scheme`
 * and auto-switches when the OS toggles.
 *
 * Principle 10.2: switching is instant, zero JS re-mount — CSS variables
 * re-resolve on attribute change.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = usePreferences((s) => s.theme)
  const density = usePreferences((s) => s.density)
  const hydrated = usePreferences((s) => s.hydrated)
  const hydrate = usePreferences((s) => s.hydrate)

  useEffect(() => {
    if (!hydrated) void hydrate()
  }, [hydrated, hydrate])

  useEffect(() => {
    const root = document.documentElement
    const mql = window.matchMedia('(prefers-color-scheme: dark)')

    const apply = () => {
      const resolved = theme === 'system' ? (mql.matches ? 'dark' : 'light') : theme
      root.setAttribute('data-theme', resolved)
    }

    apply()

    if (theme !== 'system') return undefined
    mql.addEventListener('change', apply)
    return () => mql.removeEventListener('change', apply)
  }, [theme])

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density)
  }, [density])

  return <>{children}</>
}
